import { fetchConfig } from '~/global';
import { publish } from 'lib/pubsub';
import { lineItemPropStrings, PUB_SUB_EVENTS } from 'lib/constants';
import { getSecureRandomInt } from '~/utils';
import { formatDate, getEasternDate, isPrivatePreorderDate, isGeneralPreorderDate } from '~/utils';
import { isProductPreorderActive } from '~/utils/calendar-utils';
import { defineElement, getSearchParam } from '~/utils/gw-utils';
import { getCartSectionsToRender } from '~/utils/cart-utils';
import { DeliveryDateService } from '~/services/delivery-date-2';
import { CSVToArray } from '~/utils/calendar-utils';
import { calculateShippingCharge } from '~/utils/fee-utils-2';
import { getShipTestVariant } from '~/utils/shiptest-3';
import RequestQueue from '~/utils/request-queue';

const EDIT_ORDER_STORAGE_KEY = 'boarderie_edit_order';
const EDIT_ORDER_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes

class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.variantIdInput.disabled = false;
    // this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    this.standardSubmitButton = this.querySelector('.product-form__submit');
    this.standardSubmitButtonText = this.standardSubmitButton.querySelector('span');

    this.egiftSubmitButton = this.querySelector('[data-egift-submit]') || this.closest('product-info').querySelector('[data-egift-submit]');
    this.egiftSubmitButtonText = this.egiftSubmitButton?.querySelector('span');

    if (document.querySelector('cart-drawer')) {
      this.standardSubmitButton.setAttribute('aria-haspopup', 'dialog');
      this.egiftSubmitButton?.setAttribute('aria-haspopup', 'dialog');
    }

    this.standardSubmitButton.addEventListener('click', this.onSubmitHandler.bind(this));
    this.egiftSubmitButton?.addEventListener('click', this.onSubmitHandler.bind(this));

    document.querySelector('[data-send-as-an-egift-popup]')?.addEventListener('click', () => {
      this.egiftSubmitButton?.click();
    });

    this.hideErrors = this.dataset.hideErrors === 'true';

    this._editMode = false;
    this._editData = null;
    this._initEditMode();
  }

  _initEditMode() {
    if (getSearchParam('edit_cart') !== 'true') return;

    const editDataRaw = sessionStorage.getItem(EDIT_ORDER_STORAGE_KEY);
    if (!editDataRaw) return;

    try {
      this._editData = JSON.parse(editDataRaw);

      if (Date.now() - this._editData.timestamp > EDIT_ORDER_EXPIRY_MS) {
        sessionStorage.removeItem(EDIT_ORDER_STORAGE_KEY);
        this._editData = null;
        return;
      }

      this._editMode = true;
      this._updateButtonForEditMode();
      this._restoreCustomizations();
    } catch (e) {
      console.error('[ProductForm] Failed to parse edit order data:', e);
      this._editData = null;
    }
  }

  _updateButtonForEditMode() {
    const updateText = window.variantStrings?.updateOrder || 'Update Order';
    if (this.standardSubmitButtonText) {
      this.standardSubmitButtonText.textContent = updateText;
    }
  }

  _restoreCustomizations() {
    const props = this._editData?.preservedProperties;
    if (!props) return;

    // Restore Recipient Age
    if (props['Recipient Age']) {
      const ageInput = document.getElementById('age');
      if (ageInput) ageInput.value = props['Recipient Age'];
    }

    // Restore Initials (stored as "A,B" in the Initials property)
    if (props['Initials']) {
      const [initial1, initial2] = props['Initials'].split(',');
      const select1 = document.getElementById('recipient-initials-1');
      const select2 = document.getElementById('recipient-initials-2');
      if (select1 && initial1) select1.value = initial1.trim();
      if (select2 && initial2) select2.value = initial2.trim();
    }
  }

  async _handleEditSubmit(evt) {
    const formData = new FormData(this.form);
    const currentTarget = evt.currentTarget;

    const validationResult = await this.#validateCustomizations(formData);
    if (validationResult === 'cancel') return;

    const context = this.#extractFormContext(formData, currentTarget);

    if (this.#isButtonDisabled(context.isEgift)) return;

    this.handleErrorMessage();
    this.#setLoadingState(context.isEgift, true);

    try {
      // Step 1: Remove old parent + its addons from cart
      await this._removeOldCartItems();

      // Step 2: Build new item groups with preserved properties
      const itemGroups = this.#buildItemGroups(formData, context);
      this._mergePreservedProperties(itemGroups);

      // Step 3: Add new items to cart
      const config = this.#buildRequestConfig();
      const queue = new RequestQueue();

      itemGroups.forEach((group, index) => {
        const isLastGroup = index === itemGroups.length - 1;

        queue.enqueue(
          () => this.#submitItemGroup(group, config),
          isLastGroup
            ? (finalResponse) => {
              this.#setLoadingState(context.isEgift, false);

              if (!finalResponse || finalResponse.status) {
                const message = finalResponse?.description || 'Failed to update order. Please try again.';
                this.handleErrorMessage(message);
                return;
              }

              sessionStorage.removeItem(EDIT_ORDER_STORAGE_KEY);
              this._editMode = false;
              window.location = window.routes.cart_url;
            }
            : null
        );
      });
    } catch (error) {
      console.error('[ProductForm] Edit submit failed:', error);
      this.#setLoadingState(context.isEgift, false);
      this.handleErrorMessage('Failed to update order. Please try again.');
    }
  }

  async _removeOldCartItems() {
    const { cartItemKey, groupKey } = this._editData;

    const cartResponse = await fetch('/cart.js');
    if (!cartResponse.ok) {
      throw new Error(`Failed to fetch cart: ${cartResponse.status}`);
    }
    const cart = await cartResponse.json();

    const updates = {};

    // Remove the parent item
    const parentItem = cart.items.find((i) => i.key === cartItemKey);
    if (parentItem) {
      updates[parentItem.key] = 0;
    }

    // Remove ALL items sharing the same groupKey (addons, legacy fee items, etc.)
    cart.items.forEach((item) => {
      if (item.properties?._groupKey === groupKey && item.key !== cartItemKey) {
        updates[item.key] = 0;
      }
    });

    if (Object.keys(updates).length === 0) return;

    const removeResponse = await fetch(routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    });

    if (!removeResponse.ok) {
      throw new Error(`Failed to remove old cart items: ${removeResponse.status}`);
    }
  }

  _mergePreservedProperties(itemGroups) {
    if (!itemGroups.length || !itemGroups[0]?.length || !this._editData?.preservedProperties) return;

    const parentItem = itemGroups[0][0];
    const preserved = this._editData.preservedProperties;

    // Properties set only on the cart page — merge if not already set
    const cartOnlyProperties = [
      '_message',
      '_from',
      '_message_field_enabled',
      'Address',
      '_address-first-name',
      '_address-last-name',
      '_address-address',
      '_address-address1',
      '_address-address2',
      '_address-city',
      '_address-state',
      '_address-country',
      '_address-province',
      '_address-zip',
      '_address-phone',
      '_address-email',
      'Recipient email',
      '_note',
      '_first-name',
      '_last-name',
    ];

    cartOnlyProperties.forEach((key) => {
      if (preserved[key] != null && preserved[key] !== '' && parentItem.properties[key] == null) {
        parentItem.properties[key] = preserved[key];
      }
    });

    // Delivery date properties — force-overwrite with preserved values so the user's
    // chosen date survives the edit (otherwise #applyPhysicalDeliveryProperties recalculates)
    const deliveryDateProperties = [
      'Arrival Date',
      '_arrival-date-in-iso',
      '_Ship By',
      '_picked-a-date',
      '_shipping_charge',
    ];

    const hasPreservedDeliveryDate = deliveryDateProperties.some(
      (key) => preserved[key] != null && preserved[key] !== ''
    );

    if (hasPreservedDeliveryDate) {
      deliveryDateProperties.forEach((key) => {
        if (preserved[key] != null && preserved[key] !== '') {
          parentItem.properties[key] = preserved[key];
        }
      });
    }
  }

  get variantIdInput() {
    return this.form.querySelector('[name=id]');
  }

  async onSubmitHandler(evt) {
    if (this._editMode) {
      return this._handleEditSubmit(evt);
    }

    const formData = new FormData(this.form);
    const currentTarget = evt.currentTarget;

    // Validate customizations (age, initials)
    const validationResult = await this.#validateCustomizations(formData);
    if (validationResult === 'cancel') {
      return;
    }

    // Extract context from form data
    const context = this.#extractFormContext(formData, currentTarget);

    // Check if button is disabled
    if (this.#isButtonDisabled(context.isEgift)) {
      return;
    }

    // Clear errors and show loading state
    this.handleErrorMessage();
    this.#setLoadingState(context.isEgift, true);

    // Set active element for cart before async operations
    if (this.cart) {
      this.cart.setActiveElement(document.activeElement);
    }

    // Build request configuration
    const config = this.#buildRequestConfig();

    // Build item groups (each group = parent + its children for one quantity)
    const itemGroups = this.#buildItemGroups(formData, context);

    // Use RequestQueue to submit each group sequentially
    // This prevents race conditions with Shopify's cart API
    const queue = new RequestQueue();

    // Enqueue all item groups - the callback on the last one will be called
    // after all requests complete with the final response (latestResponse)
    itemGroups.forEach((group, index) => {
      const isLastGroup = index === itemGroups.length - 1;

      queue.enqueue(
        () => this.#submitItemGroup(group, config),
        isLastGroup
          ? (finalResponse) => {
            this.#setLoadingState(context.isEgift, false);

            if (!finalResponse) {
              console.error('No response received from cart API');
              return;
            }

            this.#handleCartResponse(finalResponse, formData, context.isEgift);
          }
          : null
      );
    });
  }

  /**
   * Validates customization options (age, initials) before submission
   * @param {FormData} formData - The form data to validate
   * @returns {Promise<string>} - 'cancel' if user cancels, 'valid' or 'skip' otherwise
   */
  async #validateCustomizations(formData) {
    // Age Customization option
    if (formData.has('properties[Recipient Age]')) {
      const recipientAge = formData.get('properties[Recipient Age]');
      const validationResult = await this.validateRecipientAge(recipientAge);
      if (validationResult === 'cancel') {
        return 'cancel';
      }
    }

    // Initials Customization option - combine two fields into one
    if (formData.has('properties[Initials1]') && formData.has('properties[Initials2]')) {
      const initials1 = formData.get('properties[Initials1]');
      const initials2 = formData.get('properties[Initials2]');
      formData.append('properties[Initials]', `${initials1},${initials2}`);
    }

    return 'valid';
  }

  /**
   * Toggles loading state on submit buttons
   * @param {boolean} isEgift - Whether this is an e-gift submission
   * @param {boolean} loading - Whether to show or hide loading state
   */
  #setLoadingState(isEgift, loading) {
    if (loading) {
      if (isEgift) {
        this.egiftSubmitButton?.setAttribute('aria-disabled', true);
        this.egiftSubmitButton?.classList.add('loading');
        this.egiftSubmitButton?.querySelector('.loading__spinner')?.classList.remove('hidden');

        document
          .querySelector('[data-send-as-an-egift-popup]')
          ?.querySelector('.loading__spinner')
          ?.classList.remove('hidden');
      } else {
        this.standardSubmitButton?.setAttribute('aria-disabled', true);
        this.standardSubmitButton?.classList.add('loading');
        this.standardSubmitButton?.querySelector('.loading__spinner')?.classList.remove('hidden');
      }
    } else {
      if (isEgift) {
        this.egiftSubmitButton?.classList.remove('loading');
      } else {
        this.standardSubmitButton?.classList.remove('loading');
      }

      if (this.cart && this.cart.classList.contains('is-empty')) {
        this.cart.classList.remove('is-empty');
      }

      if (!this.error) {
        if (isEgift) {
          this.egiftSubmitButton?.removeAttribute('aria-disabled');
        } else {
          this.standardSubmitButton?.removeAttribute('aria-disabled');
        }
      }

      this.querySelectorAll('.loading__spinner').forEach((spinner) => {
        spinner.classList.add('hidden');
      });

      document
        .querySelector('[data-send-as-an-egift-popup]')
        ?.querySelector('.loading__spinner')
        ?.classList.add('hidden');
    }
  }

  /**
   * Builds the fetch configuration for the cart API request
   * @returns {Object} - Fetch config object
   */
  #buildRequestConfig() {
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    return config;
  }

  /**
   * Extracts context from form data and event for use throughout the submit handler
   * @param {FormData} formData - The form data
   * @param {HTMLElement} currentTarget - The clicked button
   * @returns {Object} - Context object with extracted values
   */
  #extractFormContext(formData, currentTarget) {
    const isDessertBoard = formData.get('dessert-board') === 'true';

    return {
      isDessertBoard,
      boardType: isDessertBoard ? 'dessert-board' : 'cheese-board',
      isEgift: currentTarget === this.egiftSubmitButton,
      isCorporateGift: formData.get('properties[_corporate-gift]') === 'true',
      freshboardVariantId: formData.get('properties[_board-variant-id]'),
      egiftVariantId: formData.get('properties[_egift-variant-id]'),
      quantity: parseInt(formData.get('quantity'), 10) || 1,
    };
  }

  /**
   * Checks if the submit button is currently disabled
   * @param {boolean} isEgift - Whether this is an e-gift submission
   * @returns {boolean} - True if button is disabled
   */
  #isButtonDisabled(isEgift) {
    if (isEgift) {
      return this.egiftSubmitButton?.getAttribute('aria-disabled') === 'true';
    }
    return this.standardSubmitButton.getAttribute('aria-disabled') === 'true';
  }

  /**
   * Applies e-gift specific properties to an item (today's date as delivery)
   * @param {Object} item - The line item to modify
   */
  #applyEgiftProperties(item) {
    const todayInEST = getEasternDate();
    const formattedToday = formatDate({
      date: todayInEST.date,
      separator: '/',
    });

    item.properties['_e-gift'] = 'true';
    item.properties['_Ship By'] = formattedToday;
    item.properties['Arrival Date'] = formattedToday;
    item.properties['_arrival-date-in-iso'] = todayInEST.date?.toISOString();
  }

  /**
   * Applies physical delivery properties to an item (delivery dates, fees, preorder)
   * @param {Object} item - The line item to modify
   * @param {Array} items - The items array to add fee products to
   * @param {Object} context - The form context
   * @returns {void}
   */
  #applyPhysicalDeliveryProperties(item, items, context) {
    let blockoutDates = this.form.querySelector('[data-product-block-out-dates]');
    let soldoutDates = this.form.querySelector('[data-product-sold-out-dates]');

    if (blockoutDates && blockoutDates?.value?.trim()) {
      blockoutDates = CSVToArray(blockoutDates.value.trim());
    }

    if (soldoutDates && soldoutDates?.value?.trim()) {
      soldoutDates = CSVToArray(soldoutDates.value.trim());
    }

    const deliveryDateService = new DeliveryDateService(window.Boarderie, blockoutDates, soldoutDates);
    const {
      deliveryDate,
      deliveryDateISO,
      shippingDate,
      nonPremiumDeliveryDate,
      nonPremiumDeliveryDateISO,
      nonPremiumShippingDate,
    } = deliveryDateService;

    let arrivalDate, arrivalDateISO, shipByDate;

    // Determine arrival date and shipping date based on board type and delivery type
    if (context.isDessertBoard) {
      // Dessert board with standard delivery: use non-premium dates (ship 2 days before)
      arrivalDate = nonPremiumDeliveryDate;
      arrivalDateISO = nonPremiumDeliveryDateISO;
      shipByDate = nonPremiumShippingDate;

      // Add dessert board and overnight delivery line item property
      item.properties[lineItemPropStrings.isDessertBoard] = 'true';
      item.properties[lineItemPropStrings.isOvernightDelivery] = 'false';
    } else {

      // Other boards: use standard delivery dates (ship 1 day before)
      arrivalDate = deliveryDate;
      arrivalDateISO = deliveryDateISO;
      shipByDate = shippingDate;
    }

    item.properties['Arrival Date'] = formatDate({ date: arrivalDate, separator: '/' });
    item.properties['_arrival-date-in-iso'] = arrivalDateISO;
    item.properties['_Ship By'] = formatDate({ date: shipByDate, separator: '/' });

    // If the next delivery date is a fee day, add a fee product to the cart form data
    const matchingFees = deliveryDateService.getFees(arrivalDate, context.boardType);

    // Handle preorder
    this.handlePreorder(item.properties, arrivalDate);

    // Handle product-level preorder
    if (isProductPreorderActive()) {
      item.properties['_product-preorder'] = 'true';
    }

    // Store shipping charge as a line item property on the main product (in dollars)
    const shippingCharge = calculateShippingCharge(matchingFees);
    if (shippingCharge) {
      item.properties[lineItemPropStrings.shippingCharge] = shippingCharge;
    }
  }

  /**
   * Applies addon properties to an item and adds addon line items
   * @param {Object} item - The main line item
   * @param {FormData} formData - The form data
   * @param {Array} items - The items array to add addons to
   * @param {Object} context - The form context
   */
  #applyAddonProperties(item, formData, items, context) {
    const addonIds = [];
    const addons = formData.get('properties[add-on]');
    const addonsArray = addons ? addons.split(',') : [];
    const isPromoAddon = formData.get('properties[_promo-addon]') === 'true';

    if (addonsArray.length) {
      addonsArray.forEach((addon) => {
        const [addonIdPhysical, addonIdEgift] = addon.split('::');
        const addonId = context.isEgift ? addonIdEgift : addonIdPhysical;

        addonIds.push(addonId);

        const addonProperties = {
          _connected: 'true',
          _groupKey: item.properties._groupKey,
          _type: 'addon',
          _addon_physical_id: addonIdPhysical,
          _addon_egift_id: addonIdEgift,
        };

        if (isPromoAddon) {
          addonProperties['_free_addon'] = 'true';
        }

        items.push({
          id: addonId,
          quantity: 1,
          parent_id: item.id,
          properties: addonProperties,
        });
      });

      item.properties[lineItemPropStrings.addOn] = addonIds.join(',');
      item.properties[lineItemPropStrings.addOnProductAdded] = true;
    }
  }

  /**
   * Builds item groups for cart submission - each group contains a parent item and its children
   * @param {FormData} formData - The form data
   * @param {Object} context - The form context
   * @returns {Array<Array>} - Array of item groups, each group is [parentItem, ...childItems]
   */
  #buildItemGroups(formData, context) {
    const propertiesToTransfer = [
      '_type',
      '_product-id',
      '_isFee',
      '_board-variant-id',
      '_egift-variant-id',
      '_corporate-gift',
      'Recipient Age',
      'Initials1',
      'Initials2',
      'Initials',
      '_promo-addon',
    ];

    const itemGroups = [];
    const uniqueGroupKey = getSecureRandomInt();

    for (let i = 1; i <= context.quantity; i++) {
      // Each iteration creates one group: parent + its children
      const groupItems = [];

      const item = {
        id: context.isEgift ? context.egiftVariantId : context.freshboardVariantId,
        quantity: 1,
        properties: {
          _groupKey: getSecureRandomInt(),
        },
      };

      if (context.isCorporateGift) {
        item.properties._corporateGroupKey = uniqueGroupKey;
      }

      // Transfer properties from form data to item
      propertiesToTransfer.forEach((property) => {
        const value = formData.get('properties[' + property + ']');
        if (value) {
          item.properties[property] = value;
        }
      });

      item.properties._shiptest = getShipTestVariant();

      // Apply type-specific properties (may add child items to groupItems)
      if (context.isEgift) {
        this.#applyEgiftProperties(item);
      } else {
        this.#applyPhysicalDeliveryProperties(item, groupItems, context);
      }

      // Apply addon properties (may add child items to groupItems)
      this.#applyAddonProperties(item, formData, groupItems, context);

      // Parent item goes first, then children
      itemGroups.push([item, ...groupItems]);
    }

    // Clean up transferred properties from form data
    propertiesToTransfer.forEach((property) => {
      formData.delete('properties[' + property + ']');
    });

    return itemGroups;
  }

  /**
   * Creates a FormData object for a single item group
   * @param {Array} itemGroup - Array of items [parentItem, ...childItems]
   * @returns {FormData} - FormData ready for submission
   */
  #createFormDataForGroup(itemGroup) {
    const formData = new FormData();

    // Add cart sections for rendering
    if (this.cart) {
      formData.append(
        'sections',
        getCartSectionsToRender().map((section) => section.section)
      );
      formData.append('sections_url', window.location.pathname);
    }

    // Append items to form data
    itemGroup.forEach((item, i) => {
      formData.append('items[' + i + '][id]', item.id);
      formData.append('items[' + i + '][quantity]', item.quantity);

      if (item.parent_id) {
        formData.append('items[' + i + '][parent_id]', item.parent_id);
      }

      Object.keys(item.properties).forEach((property) => {
        formData.append('items[' + i + '][properties][' + property + ']', item.properties[property]);
      });
    });

    return formData;
  }

  /**
   * Submits a single item group to the cart API
   * @param {Array} itemGroup - Array of items [parentItem, ...childItems]
   * @param {Object} config - Fetch config object
   * @returns {Promise} - Promise resolving to the API response
   */
  #submitItemGroup(itemGroup, config) {
    const formData = this.#createFormDataForGroup(itemGroup);
    const requestConfig = { ...config, body: formData };

    return fetch(`${routes.cart_add_url}`, requestConfig).then((response) => response.json());
  }

  /**
   * Dispatches customer account event for saved items tracking
   */
  #dispatchCustomerAccountEvent() {
    const gwCustomerAccounts = document.querySelector('gw-customer-accounts');
    if (gwCustomerAccounts) {
      const event = new CustomEvent('gw:saved-items-from-cart', {
        detail: {
          productId: this.form.querySelector('[name="product-id"]').value,
          variantId: this.form.querySelector('[name="id"]').value,
        },
      });

      gwCustomerAccounts.dispatchEvent(event);
    }
  }

  /**
   * Tracks add-to-cart event for Klaviyo analytics
   * @param {Object} response - The cart API response
   */
  #trackKlaviyoEvent(response) {
    const addedItem = response.items[0];

    const item = {
      Name: addedItem.title,
      ProductID: addedItem.product_id,
      ImageURL: addedItem.image,
      URL: window.location.host + addedItem.url,
      Brand: addedItem.vendor,
      Price: Shopify.formatMoney(addedItem.price),
      Value: addedItem.price / 100,
      CompareAtPrice: '$0.00',
    };

    if (addedItem.compare_at_price) {
      item['CompareAtPrice'] = Shopify.formatMoney(addedItem.compare_at_price);
    }

    document.documentElement.dispatchEvent(
      new CustomEvent('product:added', {
        bubbles: true,
        detail: {
          variant: item.id,
          quantity: 1,
          item: item,
        },
      })
    );
  }

  /**
   * Handles the cart API response (success or error)
   * @param {Object} response - The cart API response
   * @param {FormData} formData - The form data
   * @param {boolean} isEgift - Whether this is an e-gift submission
   */
  #handleCartResponse(response, formData, isEgift) {
    if (response.status) {
      // Error response
      publish(PUB_SUB_EVENTS.cartError, {
        source: 'product-form',
        productVariantId: formData.get('id'),
        errors: response.errors || response.description,
        message: response.message,
      });
      this.handleErrorMessage(response.description);

      if (isEgift) {
        const soldOutMessage = this.egiftSubmitButton?.querySelector('.sold-out-message');
        if (!soldOutMessage) return;
        this.egiftSubmitButton?.setAttribute('aria-disabled', true);
        this.egiftSubmitButtonText?.classList.add('hidden');
        soldOutMessage?.classList.remove('hidden');
      } else {
        const soldOutMessage = this.standardSubmitButton.querySelector('.sold-out-message');
        if (!soldOutMessage) return;
        this.standardSubmitButton.setAttribute('aria-disabled', true);
        this.standardSubmitButtonText.classList.add('hidden');
        soldOutMessage.classList.remove('hidden');
      }

      this.error = true;
      return;
    }

    // Success response
    this.#dispatchCustomerAccountEvent();
    this.#trackKlaviyoEvent(response);

    if (!this.cart) {
      window.location = window.routes.cart_url;
      return;
    }

    if (!this.error) {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'product-form',
        productVariantId: formData.get('id'),
        cartData: response,
      });
    }

    this.error = false;
    this.cart.renderContents(response);
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return;

    this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
    if (!this.errorMessageWrapper) return;
    this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

    this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

    if (errorMessage) {
      this.errorMessage.textContent = errorMessage;
    }
  }

  toggleSubmitButton(disable = true, text) {
    const isProductPreorder = this.standardSubmitButton?.hasAttribute('data-product-preorder');

    if (disable) {
      this.egiftSubmitButton?.setAttribute('disabled', 'disabled');
      this.standardSubmitButton.setAttribute('disabled', 'disabled');

      if (text) {
        if (this.egiftSubmitButtonText) this.egiftSubmitButtonText.textContent = text;
        this.standardSubmitButtonText.textContent = text;
      }
    } else {
      this.egiftSubmitButton?.removeAttribute('disabled');
      this.standardSubmitButton.removeAttribute('disabled');

      if (this._editMode) {
        this.standardSubmitButtonText.textContent = window.variantStrings?.updateOrder || 'Update Order';
      } else if (isProductPreorder) {
        const textSpan = this.standardSubmitButton.querySelector('.product-form__submit-text');
        if (textSpan) {
          textSpan.textContent = window.variantStrings.preOrderBoard || 'PRE-ORDER BOARD';
        }
      } else {
        this.standardSubmitButtonText.textContent = window.variantStrings.addToCart;
      }
      if (this.egiftSubmitButtonText) {
        this.egiftSubmitButtonText.textContent = this._editMode
          ? (window.variantStrings?.updateOrder || 'Update Order')
          : window.variantStrings.addToCart;
      }
    }
  }

  handlePreorder(properties, arrivalDate) {
    let isPreorder = false;
    let preorderDiscount = 0;
    let preorderDiscountType = '';

    const isPrivatePreorder = isPrivatePreorderDate(arrivalDate);
    if (isPrivatePreorder) {
      isPreorder = true;
      preorderDiscount = window.Boarderie?.privatePreorder?.discount;
      preorderDiscountType = window.Boarderie?.privatePreorder?.discountType;

      properties[lineItemPropStrings.privatePreorder] = 'true';
      delete properties[lineItemPropStrings.generalPreorder];
    }

    const isGeneralPreorder = isGeneralPreorderDate(arrivalDate);
    if (isGeneralPreorder) {
      isPreorder = true;
      preorderDiscount = window.Boarderie?.generalPreorder?.discount;
      preorderDiscountType = window.Boarderie?.generalPreorder?.discountType;

      properties[lineItemPropStrings.generalPreorder] = 'true';
      delete properties[lineItemPropStrings.privatePreorder];
    }

    if (isPreorder) {
      properties[lineItemPropStrings.isPreorder] = 'true';
      properties[lineItemPropStrings.preorderDiscountAmount] = preorderDiscount;
      properties[lineItemPropStrings.preorderDiscountType] = preorderDiscountType;
    } else {
      delete properties[lineItemPropStrings.isPreorder];
      delete properties[lineItemPropStrings.preorderDiscountAmount];
      delete properties[lineItemPropStrings.preorderDiscountType];
      delete properties[lineItemPropStrings.privatePreorder];
      delete properties[lineItemPropStrings.generalPreorder];
    }
  }

  validateRecipientAge(age) {
    return new Promise((resolve) => {
      if (!age || isNaN(age)) {
        const modal = document.createElement('div');
        modal.classList.add('age-validation-modal');
        modal.innerHTML = `
              <div class="modal-content">
                <h3 class="modal__heading">${window.variantStrings.ageValidationModalHeading}</h3>
                <button class="modal__button button button--primary" id="goBackButton">${window.variantStrings.ageValidationGoBackButton}</button>
                <button class="modal__button button button--secondary" id="skipAddToCartButton">${window.variantStrings.ageValidationSkipButton}</button>
              </div>
            `;

        document.body.appendChild(modal);
        document.body.classList.add('overflow-hidden');

        modal.querySelector('#skipAddToCartButton').addEventListener('click', () => {
          modal.remove();
          resolve('skip');
        });

        modal.querySelector('#goBackButton').addEventListener('click', () => {
          document.body.classList.remove('overflow-hidden');
          modal.remove();
          resolve('cancel');
        });
      } else {
        resolve('valid');
      }
    });
  }
}


defineElement('product-form', ProductForm);

class GiftCardForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.variantIdInput.disabled = false;
    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    this.submitButton = this.querySelector('[type="submit"]');
    this.submitButtonText = this.submitButton.querySelector('span');

    if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

    this.hideErrors = this.dataset.hideErrors === 'true';
  }

  onSubmitHandler(evt) {
    evt.preventDefault();
    if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

    this.handleErrorMessage();

    this.submitButton.setAttribute('aria-disabled', true);
    this.submitButton.classList.add('loading');
    this.querySelector('.loading__spinner').classList.remove('hidden');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData(this.form);
    if (this.cart) {
      formData.append(
        'sections',
        getCartSectionsToRender().map((section) => section.section)
      );
      formData.append('sections_url', window.location.pathname);
      this.cart.setActiveElement(document.activeElement);
    }
    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        // Dispatch saved cart item event for customer accounts
        const gwCustomerAccounts = document.querySelector('gw-customer-accounts');
        if (gwCustomerAccounts) {
          const event = new CustomEvent('gw:saved-items-from-cart', {
            detail: {
              productId: this.form.querySelector('[name="product-id"]').value,
              variantId: this.form.querySelector('[name="id"]').value,
              target: this.form,
            },
          });

          gwCustomerAccounts.dispatchEvent(event);
        }

        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: response.errors || response.description,
            message: response.message,
          });
          this.handleErrorMessage(response.description);

          const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
          if (!soldOutMessage) return;
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButtonText.classList.add('hidden');
          soldOutMessage.classList.remove('hidden');
          this.error = true;
          return;
        } else if (!this.cart) {
          window.location = window.routes.cart_url;
          return;
        }

        if (!this.error)
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: response,
          });

        this.error = false;
        this.cart.renderContents(response);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.submitButton.classList.remove('loading');
        if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
        if (!this.error) this.submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading__spinner').classList.add('hidden');
      });
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return;

    this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
    if (!this.errorMessageWrapper) return;
    this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

    this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

    if (errorMessage) {
      this.errorMessage.textContent = errorMessage;
    }
  }

  toggleSubmitButton(disable = true, text) {
    if (disable) {
      this.submitButton.setAttribute('disabled', 'disabled');
      if (text) this.submitButtonText.textContent = text;
    } else {
      this.submitButton.removeAttribute('disabled');
      this.submitButtonText.textContent = window.variantStrings.addToCart;
    }
  }

  get variantIdInput() {
    return this.form.querySelector('[name=id]');
  }
}

defineElement('gift-card-form', GiftCardForm);
